/* ─────────────────────────────────────────────────────────────────────────────
   KCI Event Tagging — RTK Query slice (single data layer for the admin module).

   Backend contract: /api/kci/* (branch PROSHIP-1261, KCI_API_DOC). Every endpoint
   is wired to the real transport — no mocks/dummy data. The courier-partner and
   merchant pickers are NOT part of this module: those reuse the app's existing
   cached lists (commonApiCalls) and only feed cpAccountCode / merchantOid here.

   Transport mirrors rvpQcApiSlice / d2cApiSlice: shared get/post/putGeneric/
   postMultipart from constants.js (ENDPOINT = /api/ base). Responses are raw JSON.
   ──────────────────────────────────────────────────────────────────────────── */
import { createApi } from '@reduxjs/toolkit/query/react'
import { apiConstants, get, post, putGeneric, postMultipart } from '../common/constants'

const INTIMATION = apiConstants.KCI_INTIMATION   // 'kci/intimation'
const EVENT = apiConstants.KCI_EVENT             // 'kci/event'
const GEO = apiConstants.KCI_GEO                 // 'kci/geo'
const TAGGED = apiConstants.KCI_TAGGED_ORDERS    // 'kci/tagged-orders'
const DASH = apiConstants.KCI_DASHBOARD          // 'kci/dashboard'
const CONFIG = apiConstants.KCI_CONFIG           // 'kci/config'

/* axios call → RTK Query {data} | {error}. Returns the raw response body.
   The error carries status + server message so the UI can show 409-overlap /
   400-validation strings verbatim (KCI_API_DOC §4.2, §5.3). */
const run = async (promise) => {
  try {
    const res = await promise
    const body = res?.data
    // Proship wraps most responses in a GenericAPIResponse envelope { data: ... }.
    // KCI's own payloads never carry a top-level "data" key, so unwrap it when present
    // (works whether the backend wraps or returns the bare payload per the API doc).
    const payload = (body && typeof body === 'object' && !Array.isArray(body) && 'data' in body) ? body.data : body
    return { data: payload }
  } catch (err) {
    const r = err?.response
    return { error: { status: r?.status, data: r?.data, message: r?.data?.message || r?.data || err.message } }
  }
}

export const kciApiSlice = createApi({
  reducerPath: 'kciApi',
  baseQuery: () => ({ data: undefined }), // unused — every endpoint defines its own queryFn
  tagTypes: ['Intimations', 'Events', 'Event', 'MerchantConfig'],
  endpoints: (b) => ({
    // ══ Create · Step 1 — CP Intimation ══════════════════════════════════════
    // §3.1 multipart, field name `file` → { fileUrl }
    uploadAttachment: b.mutation({
      queryFn: (file) => {
        const fd = new FormData()
        fd.append('file', file)
        return run(postMultipart(`${INTIMATION}/attachment`, fd))
      },
    }),
    // §3.2 keyword auto-suggest → { suggested, displayName }
    suggestReason: b.mutation({
      queryFn: ({ subject, body }) => run(post(`${INTIMATION}/suggest-reason`, { subject, body })),
    }),
    // §3.3 log an intimation → full intimation (status PENDING_EVENT_CREATION)
    createIntimation: b.mutation({
      queryFn: (body) => run(post(INTIMATION, body)),
      invalidatesTags: ['Intimations'],
    }),
    // §3.4 pending intimations (newest first) — "continue from pending" dropdown
    pendingIntimations: b.query({
      queryFn: ({ limit = 50 } = {}) => run(get(`${INTIMATION}/pending`, { limit })),
      providesTags: ['Intimations'],
    }),
    // §3.5 list intimations
    listIntimations: b.query({
      queryFn: (params = {}) => run(get(`${INTIMATION}/list`, params)),
      providesTags: ['Intimations'],
    }),
    // §3.6 "informational only — no event needed" exit → { status: INFORMATIONAL_ONLY }
    markInformational: b.mutation({
      queryFn: (id) => run(post(`${INTIMATION}/${id}/informational`, {})),
      invalidatesTags: ['Intimations'],
    }),

    // ══ Create · Step 4 — Geography helpers ══════════════════════════════════
    geoStates: b.query({ queryFn: () => run(get(`${GEO}/states`)) }),
    geoCities: b.query({ queryFn: () => run(get(`${GEO}/cities`)) }),
    // §4.1 preview resolution → { pincodeCount, pincodes[] }
    geoResolve: b.mutation({
      queryFn: (body) => run(post(`${GEO}/resolve`, body)),
    }),

    // ══ Create · Steps 2–6 — Event ═══════════════════════════════════════════
    // §4.2 create event(s) — single object OR array (multi-courier)
    createEvent: b.mutation({
      queryFn: (body) => run(post(EVENT, body)),
      invalidatesTags: ['Events'],
    }),
    // §4.3 activate a saved draft → SCHEDULED | ACTIVE (overlap validated here → 409)
    activateEvent: b.mutation({
      queryFn: (id) => run(post(`${EVENT}/${id}/activate`, {})),
      invalidatesTags: (r, e, id) => ['Events', { type: 'Event', id }],
    }),

    // ══ Manage Events ════════════════════════════════════════════════════════
    // §5.1 search/list → { events, totalRecords, pageNumber, pageSize }
    searchEvents: b.query({
      queryFn: (params = {}) => run(get(`${EVENT}/search`, params)),
      providesTags: ['Events'],
    }),
    // §5.2 get one event
    getEvent: b.query({
      queryFn: (id) => run(get(`${EVENT}/${id}`)),
      providesTags: (r, e, id) => [{ type: 'Event', id }],
    }),
    // §5.3 lifecycle actions — EXTEND | CLOSE | RESUME_EARLY
    eventAction: b.mutation({
      queryFn: ({ id, ...body }) => run(post(`${EVENT}/${id}/action`, body)),
      invalidatesTags: (r, e, { id }) => ['Events', { type: 'Event', id }],
    }),
    // §5.4 send history (expandable row)
    eventNotifications: b.query({
      queryFn: (id) => run(get(`${EVENT}/${id}/notifications`)),
      providesTags: (r, e, id) => [{ type: 'Event', id }],
    }),
    // §5.5 resend client email → { status: RESENT }
    resendEvent: b.mutation({
      queryFn: (id) => run(post(`${EVENT}/${id}/resend`, {})),
      invalidatesTags: (r, e, id) => [{ type: 'Event', id }],
    }),
    // §5.6 manual re-tag (ACTIVE/EXTENDED only) → { newlyTagged }
    retagEvent: b.mutation({
      queryFn: (id) => run(post(`${EVENT}/${id}/retag`, {})),
      invalidatesTags: (r, e, id) => ['Events', { type: 'Event', id }],
    }),

    // ══ Tagged Orders ════════════════════════════════════════════════════════
    // §6.1 per-event tagged orders
    eventOrders: b.query({
      queryFn: ({ id, ...params }) => run(get(`${EVENT}/${id}/orders`, params)),
    }),
    // §6.2 global tagged orders (search = exact AWB or Order ID overrides other filters)
    taggedOrders: b.query({
      queryFn: (params = {}) => run(get(TAGGED, params)),
    }),
    // §6.3 on-time analysis (include/exclude KPI tiles) — from/to required
    taggedOrdersAnalysis: b.query({
      queryFn: (params = {}) => run(get(`${TAGGED}/analysis`, params)),
    }),

    // ══ Dashboard (§7) ═══════════════════════════════════════════════════════
    dashKpis: b.query({ queryFn: (params = {}) => run(get(`${DASH}/kpis`, params)) }),
    dashTrends: b.query({ queryFn: (params = {}) => run(get(`${DASH}/trends`, params)) }),
    dashReasons: b.query({ queryFn: (params = {}) => run(get(`${DASH}/reasons`, params)) }),
    dashByCourier: b.query({ queryFn: (params = {}) => run(get(`${DASH}/by-courier`, params)) }),
    dashByScope: b.query({ queryFn: (params = {}) => run(get(`${DASH}/by-scope`, params)) }),
    dashByDirection: b.query({ queryFn: (params = {}) => run(get(`${DASH}/by-direction`, params)) }),
    dashTopPincodes: b.query({ queryFn: (params = {}) => run(get(`${DASH}/top-pincodes`, params)) }),

    // ══ Merchant Notification Config (§8) ════════════════════════════════════
    merchantConfig: b.query({
      queryFn: (merchantOid) => run(get(`${CONFIG}/merchant/${merchantOid}`)),
      providesTags: (r, e, oid) => [{ type: 'MerchantConfig', id: oid }],
    }),
    saveMerchantConfig: b.mutation({
      queryFn: ({ merchantOid, body }) => run(putGeneric(`${CONFIG}/merchant/${merchantOid}`, body)),
      invalidatesTags: (r, e, { merchantOid }) => [{ type: 'MerchantConfig', id: merchantOid }],
    }),
  }),
})

export const {
  useUploadAttachmentMutation,
  useSuggestReasonMutation,
  useCreateIntimationMutation,
  usePendingIntimationsQuery,
  useListIntimationsQuery,
  useMarkInformationalMutation,
  useGeoStatesQuery,
  useGeoCitiesQuery,
  useGeoResolveMutation,
  useCreateEventMutation,
  useActivateEventMutation,
  useSearchEventsQuery,
  useGetEventQuery,
  useEventActionMutation,
  useEventNotificationsQuery,
  useResendEventMutation,
  useRetagEventMutation,
  useEventOrdersQuery,
  useTaggedOrdersQuery,
  useTaggedOrdersAnalysisQuery,
  useDashKpisQuery,
  useDashTrendsQuery,
  useDashReasonsQuery,
  useDashByCourierQuery,
  useDashByScopeQuery,
  useDashByDirectionQuery,
  useDashTopPincodesQuery,
  useMerchantConfigQuery,
  useSaveMerchantConfigMutation,
} = kciApiSlice
